import cn, { Argument as ClassName } from 'classnames'
import React, { useState } from 'react'

import {
    ChargePoint,
    Connector,
    useChargePointUpdateMutation,
    useGreenChargingChargePointStatusQuery,
} from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import { SLightIcon, SSocketIcon } from '../charge-point-ocpp-halo-control-widget'
import findChargingConnector from '../findChargingConnector'
import findSchukoConnector from '../findSchukoConnector'
import { RemoteCapiDimmerControl } from '../remote-capi-dimmer-control'
import { RemoteCapiNfcControl } from '../remote-capi-nfc-control'
import { RemoteControlRow } from '../remote-control-row'
import { RemoteControlWidget } from '../remote-control-widget'
import { toggleConnectorAvailabilityStatus } from '../toggle-connector-availability-status'
import { Toggle } from '../../common/components/Toggle'
import { notifyError } from '../../common/features/notificationsManager/Notify'
import { ClockIcon } from '../icons/clock-icon'
import ConnectorStatus from '../connector-status'
import useText from '../../common/hooks/useText'

export type ChargePointCapiHaloControlWidgetProps = {
    chargePoint: ChargePoint
    isReadOnly?: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export enum CapiConnectorMode {
    ON = 'On',
    OFF = 'Off',
    SCHEDULE = 'Schedule',
}

export function ChargePointCapiHaloControlWidget({
    chargePoint: initialChargePoint,
    isReadOnly = false,
    className,
    style,
}: ChargePointCapiHaloControlWidgetProps): React.ReactElement {
    const text = useText()
    const [chargePoint, setChargePoint] = useState(initialChargePoint)

    const [updateChargePoint] = useChargePointUpdateMutation()

    const isOnline = chargePoint.chargePointStatus !== 'Offline'

    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })

    const chargingConnector = findChargingConnector(chargePoint.connectors ?? [])
    const socketConnector = findSchukoConnector(chargePoint.connectors ?? [])

    const applyChargePoint = async (value: ChargePoint) => {
        try {
            const tempChargePoint = {
                ...chargePoint,
                ...value,
            }
            await updateChargePoint({ chargePoint: { ...chargePoint, ...value } }).unwrap()
            setChargePoint(tempChargePoint)
        } catch (_) {
            notifyError(text('msgChargerFailedToUpdate'))
        }
    }

    const toggleLightStatus = () => {
        try {
            const tempChargePoint = {
                ...chargePoint,
                downLight: !chargePoint.downLight,
            }
            updateChargePoint({
                chargePoint: tempChargePoint,
            }).unwrap()

            setChargePoint(tempChargePoint)
        } catch (_) {
            notifyError(text('msgFailToUpdateDownlight'))
        }
    }

    const applyConnectorAvailabilityStatus = (chargingConnector: Connector) => {
        try {
            const tempChargePoint = toggleConnectorAvailabilityStatus(
                chargingConnector,
                !!chargePointStatus?.greenChargingStatus?.greenChargingOn,
                chargePoint
            )
            updateChargePoint({ chargePoint: tempChargePoint })
            setChargePoint(tempChargePoint)
        } catch (_) {
            notifyError(text('msgFailToUpdateConnectorStatus'))
        }
    }

    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblHaloControlWidget'}
            className={cn(className)}
            style={style}
        >
            {chargingConnector && (
                <RemoteControlRow
                    title={<div data-testid={`charger-${chargePoint.id}`}>{text('lblCharger')}</div>}
                    button={
                        chargingConnector.mode !== CapiConnectorMode.SCHEDULE ? (
                            <Toggle
                                isToggled={chargingConnector.onByUser}
                                disabled={isReadOnly}
                                toggleFn={() => applyConnectorAvailabilityStatus(chargingConnector)}
                                data-testid={`charger-${chargePoint.id}`}
                            />
                        ) : (
                            <Toggle
                                isToggled={chargingConnector.onBySchedule}
                                toggleFn={() => applyConnectorAvailabilityStatus(chargingConnector)}
                                icon={<ClockIcon size={18} />}
                                disabled={isReadOnly}
                                data-testid={`chargerScheduling-${chargePoint.id}`}
                            />
                        )
                    }
                    status={
                        <>
                            {isOnline && (
                                <ConnectorStatus
                                    connector={chargingConnector}
                                    isOcpp={chargePoint?.isOcpp}
                                    key={chargingConnector?.connectorId}
                                    showConsumption={true}
                                />
                            )}
                        </>
                    }
                />
            )}
            {socketConnector && (
                <RemoteControlRow
                    title={
                        <>
                            <div data-testid={`socketText-${chargePoint.id}`}>
                                <Text code="lblSocket" />
                                <SSocketIcon className="icon-socket" data-testid={`socketIcon-${chargePoint.id}`} />
                            </div>
                        </>
                    }
                    button={
                        socketConnector.mode !== CapiConnectorMode.SCHEDULE ? (
                            <Toggle
                                isToggled={socketConnector.onByUser}
                                disabled={isReadOnly}
                                toggleFn={() => applyConnectorAvailabilityStatus(socketConnector)}
                                data-testid={`socket-${chargePoint.id}`}
                            />
                        ) : (
                            <Toggle
                                isToggled={socketConnector.onBySchedule}
                                toggleFn={() => applyConnectorAvailabilityStatus(socketConnector)}
                                icon={<ClockIcon size={18} />}
                                disabled={isReadOnly}
                                data-testid={`socketScheduling-${chargePoint.id}`}
                            />
                        )
                    }
                    status={
                        <div data-testid="connstate-value">
                            {isOnline && (
                                <ConnectorStatus
                                    connector={socketConnector}
                                    isOcpp={chargePoint?.isOcpp}
                                    key={chargingConnector?.connectorId}
                                />
                            )}
                        </div>
                    }
                />
            )}
            <RemoteControlRow
                title={
                    <div data-testid={`lightText-${chargePoint.id}`}>
                        <Text code="lblLight" />
                        <SLightIcon className="fa fa-lightbulb-o" data-testid={`lightIcon-${chargePoint.id}`} />
                    </div>
                }
                button={
                    <Toggle
                        isToggled={chargePoint.downLight ?? false}
                        disabled={isReadOnly}
                        toggleFn={toggleLightStatus}
                        data-testid={`light-${chargePoint.id}`}
                    />
                }
            />
            <RemoteCapiDimmerControl
                chargePoint={chargePoint}
                isReadOnly={isReadOnly}
                applyChargePoint={applyChargePoint}
            />
            {chargePoint.nfcEnabled && (
                <RemoteCapiNfcControl
                    chargePoint={chargePoint}
                    selectedConnector={chargingConnector}
                    isReadOnly={isReadOnly}
                    applyChargePoint={applyChargePoint}
                />
            )}
        </RemoteControlWidget>
    )
}
